import React from 'react';
import { Table, Typography, type TableColumnType } from 'antd';

import ButtonWithRole from '~components/ButtonWithRole';
import { PointInTime, POINT_IN_TIME_COLUMN_WIDTH } from '~components/PointInTime';
import { CustomTooltip } from '~components/Tooltip';
import { TOKEN_WRITE_REFUSED_ROLES } from '~features/auth/roleCapabilities';
import type { ConnectedCfToken } from '~features/cloudflare-integration';

export const NO_TOKENS_CONNECTED_MESSAGE = 'No Cloudflare token is connected.';

const UNREADABLE_TOKEN_MESSAGE = 'Prerender can no longer read this token. You can still disconnect it.';

const connectedCfTokenColumns = (
  onDisconnect: (domain: string) => void,
  disconnectingDomain: string | null
): TableColumnType<ConnectedCfToken>[] => [
  {
    title: 'Domain',
    key: 'domain',
    ellipsis: { showTitle: true },
    render: (_, token) => token.domain,
  },
  {
    title: 'Last four',
    key: 'lastFour',
    width: 110,
    render: (_, token) =>
      token.lastFour === null ? (
        <CustomTooltip placement="top" message={UNREADABLE_TOKEN_MESSAGE}>
          <Typography.Text type="secondary">Unreadable</Typography.Text>
        </CustomTooltip>
      ) : (
        `•••• ${token.lastFour}`
      ),
  },
  {
    title: 'Connected',
    key: 'connectedAt',
    width: POINT_IN_TIME_COLUMN_WIDTH,
    render: (_, token) => <PointInTime value={token.connectedAt} />,
  },
  {
    title: 'Last used',
    key: 'lastUsedAt',
    width: POINT_IN_TIME_COLUMN_WIDTH,
    render: (_, token) =>
      token.lastUsedAt === null ? (
        <Typography.Text type="secondary">Never used</Typography.Text>
      ) : (
        <PointInTime value={token.lastUsedAt} />
      ),
  },
  {
    title: 'Delete date',
    key: 'deleteAfter',
    width: POINT_IN_TIME_COLUMN_WIDTH,
    render: (_, token) => <PointInTime value={token.deleteAfter} />,
  },
  {
    title: 'Actions',
    key: 'actions',
    width: 120,
    render: (_, token) => (
      <ButtonWithRole
        disabledFor={TOKEN_WRITE_REFUSED_ROLES}
        danger
        type="link"
        aria-label={`Disconnect the Cloudflare token for ${token.domain}`}
        onClick={() => onDisconnect(token.domain)}
        loading={token.domain === disconnectingDomain}
        disabled={token.domain === disconnectingDomain}
      >
        Disconnect
      </ButtonWithRole>
    ),
  },
];

export type ConnectedCfTokensTableProps = {
  tokens: ConnectedCfToken[];
  onDisconnect: (domain: string) => void;
  loading?: boolean;
  loadFailed?: boolean;
  disconnectingDomain?: string | null;
};

function ConnectedCfTokensTable({
  tokens,
  onDisconnect,
  loading = false,
  loadFailed = false,
  disconnectingDomain = null,
}: ConnectedCfTokensTableProps) {
  return (
    <Table
      rowKey="domain"
      columns={connectedCfTokenColumns(onDisconnect, disconnectingDomain)}
      dataSource={tokens}
      loading={loading}
      pagination={false}
      locale={{ emptyText: loading || loadFailed ? null : NO_TOKENS_CONNECTED_MESSAGE }}
    />
  );
}

export default ConnectedCfTokensTable;
